import classNames from 'clsx'
import * as attributes from 'clsx'
import {
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { Component } from 'react'

import { withI18n } from 'shared/utils/withI18n'
import {
  CUSTOM_PATTERN_MISMATCH_MESSAGE_ATTR,
  CUSTOM_VALIDATION_MESSAGE_ATTR,
  MAX_LENGTH_VALIDATION_MESSAGE_ATTR,
  MIN_LENGTH_VALIDATION_MESSAGE_ATTR,
  NOT_EQUAL_TO_FIELD_ID_ATTR,
  NOT_EQUAL_TO_VALIDATION_MESSAGE_ATTR,
} from 'views/assets/scripts/formValidation/formControlValidation'
import ErrorMessage from 'views/components/atoms/ErrorMessage'
import IconCircleInfo from 'views/components/atoms/Icon/IconCircleInfo'
import IconCircleInfoOutline from 'views/components/atoms/Icon/IconCircleInfoOutline'
import IconEye from 'views/components/atoms/Icon/IconEye'
import IconEyeCross from 'views/components/atoms/Icon/IconEyeCross'
import Input from 'views/components/atoms/Input'
import Textarea from 'views/components/atoms/Textarea'
import ButtonTooltip from 'views/components/molecules/ButtonTooltip'
import ButtonTransparent from 'views/components/molecules/ButtonTransparent'
import { ERROR_CLASSNAME, FOCUS_OR_VALUE_CLASSNAME } from 'views/consts'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'

import { getPattern } from './helpers'

import 'views/assets/stylesheets/atoms/_inputWithClear.scss'
import './FormControlWithFloatingLabel.scss'

const renderShowPassword = () => (
  <ButtonTransparent
    type="button"
    name="show-password"
    isClientsideControlled
    data-clientside-hook="passwordReveal"
    ariaPressed="false"
    className="m-FormControlWithFloatingLabel__passwordReveal"
  >
    <IconEye className="m-FormControlWithFloatingLabel__passwordReveal--show" />
    <IconEyeCross className="m-FormControlWithFloatingLabel__passwordReveal--hide" />
    <span className="u-visuallyhidden">Toggle password visibility</span>
  </ButtonTransparent>
)

const renderTooltip = (
  tooltipText,
  isDeclarative,
  tooltipIcon = <IconCircleInfo size="small" />
) => {
  if (tooltipText) {
    const name = tooltipText.replace(/[^A-Za-z0-9]+/g, '')

    return (
      <ButtonTooltip
        id={name}
        sideRendered="left"
        text={tooltipText}
        isDeclarative={isDeclarative}
      >
        {tooltipIcon}
      </ButtonTooltip>
    )
  }

  return null
}

const renderControlIcons = ({
  formControl,
  inputType,
  tooltipText,
  tooltipIcon,
  isDeclarative,
}) => {
  if (formControl === 'input' && (inputType === 'password' || tooltipText)) {
    return (
      <div className="m-FormControlWithFloatingLabel__icon l-flex">
        {inputType === 'password' && renderShowPassword()}
        {renderTooltip(tooltipText, isDeclarative, tooltipIcon)}
      </div>
    )
  }

  return null
}

renderControlIcons.propTypes = {
  inputType: string.isRequired,
  tooltipText: string.isRequired,
  isDeclarative: string.isRequired,
}

class FormControlWithFloatingLabel extends Component {
  getOnKeyUpHandler() {
    const { onKeyUp, onClear } = this.props
    if (!onKeyUp && !onClear) {
      return null
    }

    return event => this.handleKeyUp(event)
  }

  handleKeyUp(event) {
    const { onKeyUp, onClear } = this.props
    if (typeof onClear === 'function' && event.target.value === '') {
      onClear()
    }
    if (onKeyUp) {
      return onKeyUp(event)
    }
  }

  render() {
    const {
      formControl,
      errorMessage,
      customValidityMessage,
      customPatternMismatchMessage,
      autoFocus,
      name,
      id,
      inputType,
      value,
      watch,
      defaultValue,
      disabled,
      rows,
      required,
      tooltipText,
      children,
      disableAutoCompleteAndFriends,
      onChange,
      inputClassname,
      labelClassname,
      'data-clientside-hook': dataClientsideHook,
      rounded,
      roundedLeft,
      roundedRight,
      placeholder,
      register,
      errors,
      useExternalValidator,
      adyenField,
      forceNoPattern,
      pattern: patternProp,
      list,
      autoComplete,
      minLength,
      maxLength,
      i18n,
      onBlur,
      isDeclarative,
      isNotEqualTo,
      onKeyDown,
      onFocus,
      // XXX: we have both dataQa and @[data-qa-id] in the code base, need to handle both. Has to be refactored
      dataQa,
      'data-qa-id': dataQaId,
    } = this.props
    const errorObject =
      errors &&
      errors.constructor === Object &&
      Object.entries(errors).length !== 0
        ? errors[name]
        : false

    const containerClassname = classNames('m-FormControlWithFloatingLabel', {
      [ERROR_CLASSNAME]: errorMessage || errorObject,
    })

    const formControlClassname = classNames(
      'm-FormControlWithFloatingLabel__formControl a-inputWithClear a-h3 u-font-weight--normal',
      inputClassname,
      {
        [FOCUS_OR_VALUE_CLASSNAME]:
          (value && value.length > 0) ||
          (watch && watch.length > 0) ||
          placeholder,
        'm-FormControlWithFloatingLabel__formControl--rounded-left-only':
          roundedLeft && rounded,
        'm-FormControlWithFloatingLabel__formControl--rounded-right-only':
          roundedRight && rounded,
        'm-FormControlWithFloatingLabel__formControl--rounded-none': !rounded,
        [ERROR_CLASSNAME]: errorMessage || errorObject,
        'm-FormControlWithFloatingLabel__formControl--has-icon':
          inputType === 'password' || tooltipText,
      }
    )
    const formControlLabelClassname = classNames(
      'm-FormControlWithFloatingLabel__label u-font-weight--normal',
      labelClassname
    )

    const InputComponent = formControl === 'input' ? Input : Textarea
    const actualId = id || name

    const tooltipIcon = <IconCircleInfoOutline size="small" />

    const componentDataAttributes = {
      'data-clientside-hook': attributes.default(
        'floatingLabel__formControl',
        dataClientsideHook,
        {
          'formControlValidation': !useExternalValidator,
        }
      ),
      [CUSTOM_VALIDATION_MESSAGE_ATTR]: customValidityMessage,
      [CUSTOM_PATTERN_MISMATCH_MESSAGE_ATTR]: customPatternMismatchMessage,
    }

    let errorMessageDataQaId

    if (id) {
      errorMessageDataQaId = `${id}-error-msg`
    }

    if (dataQa) {
      componentDataAttributes['data-qa-id'] = dataQa
      errorMessageDataQaId = `${dataQa}-error-msg`
    }
    if (dataQaId) {
      componentDataAttributes['data-qa-id'] = dataQaId
      errorMessageDataQaId = `${dataQaId}-error-msg`
    }

    if (minLength) {
      componentDataAttributes[MIN_LENGTH_VALIDATION_MESSAGE_ATTR] = i18n._(
        'form.error.input.minLength',
        { minLength }
      )
    }

    if (maxLength) {
      componentDataAttributes[MAX_LENGTH_VALIDATION_MESSAGE_ATTR] = i18n._(
        'form.error.input.maxLength',
        { maxLength }
      )
    }

    if (isNotEqualTo) {
      componentDataAttributes[NOT_EQUAL_TO_FIELD_ID_ATTR] = isNotEqualTo.fieldId
      componentDataAttributes[NOT_EQUAL_TO_VALIDATION_MESSAGE_ATTR] =
        isNotEqualTo.errorMessage
    }

    let pattern = forceNoPattern ? null : getPattern(inputType, name)

    if (patternProp) {
      pattern = new RegExp(patternProp).source
    }

    return (
      <label
        htmlFor={actualId}
        className={containerClassname}
        data-clientside-hook="floatingLabel formControlContainer"
      >
        {placeholder && <span className="a-visuallyhidden">{placeholder}</span>}
        <div className="FormControlWithFloatingLabel__inner">
          {adyenField ? (
            <span
              data-cse={adyenField}
              className={formControlClassname}
              name={name}
              id={actualId}
            />
          ) : (
            <InputComponent
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              className={formControlClassname}
              defaultValue={defaultValue}
              disableAutoCompleteAndFriends={disableAutoCompleteAndFriends}
              disabled={disabled}
              id={actualId}
              name={name}
              onChange={onChange}
              onKeyUp={this.getOnKeyUpHandler()}
              pattern={pattern}
              placeholder={placeholder}
              register={register}
              required={required}
              rows={rows}
              type={inputType}
              value={value}
              {...componentDataAttributes}
              list={list}
              maxLength={maxLength}
              minLength={minLength}
              onBlur={onBlur}
              onFocus={onFocus}
              onKeyDown={onKeyDown}
            />
          )}
          {children && (
            <div className={formControlLabelClassname}>
              {children}
              {required && <>*</>}
            </div>
          )}
          {renderControlIcons({
            formControl,
            inputType,
            tooltipText,
            isDeclarative,
            tooltipIcon,
          })}
        </div>
        <ErrorMessage
          data-qa-id={errorMessageDataQaId}
          text={errorMessage}
          errors={errors}
          name={name}
        />
      </label>
    )
  }
}

FormControlWithFloatingLabel.propTypes = {
  children: node,
  formControl: string,
  name: string.isRequired,
  id: string,
  value: string,
  watch: string,
  defaultValue: string,
  disabled: bool,
  inputType: string,
  errorMessage: string,
  customValidityMessage: oneOfType([object, string]),
  customPatternMismatchMessage: oneOfType([object, string]),
  rows: number,
  required: bool,
  tooltipText: string,
  disableAutoCompleteAndFriends: bool,
  onKeyUp: func,
  onClear: func,
  autoFocus: bool,
  onChange: func,
  'data-clientside-hook': string,
  inputClassname: string,
  labelClassname: string,
  rounded: bool,
  roundedLeft: bool,
  placeholder: string,
  roundedRight: bool,
  register: func,
  errors: shape({}),
  useExternalValidator: bool,
  adyenField: string,
  forceNoPattern: bool,
  pattern: string,
  list: string,
  autoComplete: string,
  minLength: oneOfType([string, number]),
  maxLength: oneOfType([string, number]),
  i18n: i18nType.isRequired,
  onBlur: func,
  isDeclarative: bool,
  isNotEqualTo: shape({
    fieldId: string.isRequired,
    errorMessage: string.isRequired,
  }),
  onKeyDown: func,
  onFocus: func,
  dataQa: string,
  'data-qa-id': string,
  publicRuntimeConfig: shape(publicRuntimeConfigType),
}

FormControlWithFloatingLabel.defaultProps = {
  'data-clientside-hook': '',
  'data-qa-id': undefined,
  adyenField: null,
  autoComplete: undefined,
  autoFocus: false,
  children: null,
  customPatternMismatchMessage: null,
  customValidityMessage: null,
  dataQa: undefined,
  disableAutoCompleteAndFriends: false,
  disabled: null,
  errorMessage: '',
  errors: null,
  forceNoPattern: false,
  formControl: 'input',
  id: null,
  inputClassname: null,
  labelClassname: null,
  inputType: 'text',
  isDeclarative: false,
  isNotEqualTo: undefined,
  list: null,
  maxLength: undefined,
  minLength: undefined,
  onBlur: null,
  onChange: null,
  onClear: null,
  onFocus: null,
  onKeyDown: null,
  onKeyUp: null,
  placeholder: null,
  register: null,
  required: null,
  rounded: true,
  roundedLeft: false,
  roundedRight: false,
  rows: null,
  tooltipText: null,
  useExternalValidator: false,
  watch: null,
  publicRuntimeConfig: {},
}

export default withI18n()(
  withShopAndRuntimeConfig(FormControlWithFloatingLabel)
)
